import axios from 'axios';
import i18n from '../i18n';

// 'sk' or 'en' for endpoints that return topic names
const apiLang = () => (String(i18n.language || 'en').toLowerCase().startsWith('sk') ? 'sk' : 'en');

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Only redirect on 401 if not on login/register endpoints
    // These endpoints are expected to return 401 for invalid credentials
    const isAuthEndpoint = error.config?.url?.includes('/auth/login') || 
                          error.config?.url?.includes('/auth/register');
    
    if (error.response?.status === 401 && !isAuthEndpoint) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export default api;

// Auth APIs
export const authAPI = {
  register: (data) => api.post('/api/auth/register', data),
  login: (data) => api.post('/api/auth/login', data),
  getMe: () => api.get('/api/auth/me'),
};

// Admin APIs
export const adminAPI = {
  // Topics
  getTopics: () => api.get('/api/admin/topics'),
  createTopic: (data) => api.post('/api/admin/topics', data),
  updateTopic: (id, data) => api.put(`/api/admin/topics/${id}`, data),
  deleteTopic: (id) => api.delete(`/api/admin/topics/${id}`),
  bulkDeleteTopics: (topicIds) => api.post('/api/admin/topics/bulk-delete', { topic_ids: topicIds }),
  bulkActivateTopics: (topicIds) => api.patch('/api/admin/topics/bulk-active', { topic_ids: topicIds }),
  bulkDeactivateTopics: (topicIds) => api.patch('/api/admin/topics/bulk-inactive', { topic_ids: topicIds }),
  
  // Questions
  getQuestions: (topicId) => api.get('/api/admin/questions', { params: { topic_id: topicId } }),
  createQuestion: (data) => api.post('/api/admin/questions', data),
  updateQuestion: (id, data) => api.put(`/api/admin/questions/${id}`, data),
  deleteQuestion: (id) => api.delete(`/api/admin/questions/${id}`),
  downloadTemplate: () => {
    return api.get('/api/admin/questions/template', { 
      responseType: 'blob'
    }).then(response => {
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'socraquest_questions_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      return response;
    });
  },
  bulkUploadQuestions: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/api/admin/questions/bulk-upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  uploadImage: (formData) => api.post('/api/admin/upload-image', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  
  // Generate single AI image
  generateAIImage: (prompt) => api.post('/api/admin/generate-ai-image', { prompt }, {
    timeout: 120000  // 2 minutes timeout for single image generation
  }),
  
  // Image Quiz with AI Generation
  downloadImageQuizTemplate: () => api.get('/api/admin/image-quiz/template', { responseType: 'blob' })
    .then(response => {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'socraquest_image_quiz_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    }),
  bulkUploadImageQuiz: (file, force = false) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/api/admin/image-quiz/bulk-upload?force=${force}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 3600000  // 60 minutes timeout for large bulk uploads (60+ images)
    });
  },
  
  // Pictures for picture questions (AI generation runs on the server in the background)
  getPictureStatus: () => api.get('/api/admin/picture-questions/status'),
  getPictureQuestions: (params) => api.get('/api/admin/picture-questions', { params }),
  generatePictures: (body) => api.post('/api/admin/picture-questions/generate', body),
  pausePictures: () => api.post('/api/admin/picture-questions/pause'),
  resumePictures: () => api.post('/api/admin/picture-questions/resume'),
  cancelPictureQueue: () => api.post('/api/admin/picture-questions/cancel-queue'),
  rejectPicture: (id) => api.post(`/api/admin/picture-questions/${id}/reject`),
  uploadQuestionPicture: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/api/admin/picture-questions/${id}/image`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  uploadPictureZip: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/api/admin/picture-questions/upload-zip', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 1800000,
    });
  },

  // Packs
  getPacks: (date) => api.get('/api/admin/packs', { params: { date } }),
  generatePack: (date) => api.post('/api/admin/packs/generate', null, { params: { date } }),
  getPackQuestions: (date) => api.get(`/api/admin/packs/${date}/questions`),
  regeneratePack: () => api.post('/api/admin/regenerate-pack'),
  getPackStatus: () => api.get('/api/admin/pack-status'),
  
  // Metrics
  getMetrics: () => api.get('/api/admin/metrics'),
  getAnalytics: (days = 14) => api.get('/api/admin/analytics', { params: { days } }),
  getQuestionReports: (status = 'open') => api.get('/api/admin/question-reports', { params: { status } }),
  resolveQuestionReport: (reportId, action) => api.post(`/api/admin/question-reports/${reportId}/resolve`, { action }),
  getSettings: () => api.get('/api/admin/settings'),
  saveSettings: (data) => api.put('/api/admin/settings', data),
  aiGenerateQuestions: (data) => api.post('/api/admin/ai/generate-questions', data),
  getUsers: (params = {}) => api.get('/api/admin/users', { params }),
  setUserStatus: (userId, isActive) => api.put(`/api/admin/users/${userId}/status`, { is_active: isActive }),
  grantBonusXp: (userId, amount) => api.post(`/api/admin/users/${userId}/bonus-xp`, { amount }),
  
  // Ads
  getAdConfig: () => api.get('/api/admin/ads/config'),
  getAdSenseStatus: () => api.get('/api/admin/ads/adsense-status'),
  updateAdConfig: (data) => api.post('/api/admin/ads/config', data),
  getManualAds: () => api.get('/api/admin/ads/manual'),
  createManualAd: (data) => api.post('/api/admin/ads/manual', data),
  updateManualAd: (id, data) => api.put(`/api/admin/ads/manual/${id}`, data),
  deleteManualAd: (id) => api.delete(`/api/admin/ads/manual/${id}`),
  uploadAdMedia: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/api/admin/ads/upload-media', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000
    });
  },
};

// User APIs
export const userAPI = {
  // Daily Pack
  getTodayPack: () => api.get('/api/packs/today', { params: { lang: apiLang() } }),
  
  // Quiz
  getQuiz: (quizIndex, lang = 'en') => api.get(`/api/quizzes/${quizIndex}`, { params: { lang } }),
  submitQuiz: (quizIndex, data) => api.post(`/api/quizzes/${quizIndex}/submit`, data),
  getQuizResult: (quizIndex) => api.get(`/api/quizzes/${quizIndex}/result`),
  getAnswers: (quizIndex, lang = 'en') => api.get(`/api/quizzes/${quizIndex}/answers`, { params: { lang } }),
  lockQuiz: (quizIndex, applyPenalty = false) => api.post(`/api/quizzes/${quizIndex}/lock`, null, { params: { apply_penalty: applyPenalty } }),
  getLeaderboard: (quizIndex, groupId) => api.get(`/api/quizzes/${quizIndex}/leaderboard`, { params: { group_id: groupId } }),
  getDailyLeaderboard: (groupId) => api.get('/api/rankings/daily', { params: { group_id: groupId } }),
  
  // Rankings - wrapper methods for RankingsPage
  getRankings: () => api.get('/api/rankings/daily').then(res => ({ data: { rankings: res.data.leaderboard || [] } })),
  getQuizRankings: (quizIndex) => api.get(`/api/quizzes/${quizIndex}/leaderboard`).then(res => ({ data: { rankings: res.data.leaderboard || [] } })),
  getDailyRankings: () => api.get('/api/rankings/daily').then(res => ({ data: { rankings: res.data.leaderboard || [] } })),
  
  // Groups
  getGroups: () => api.get('/api/groups'),
  getMyGroups: () => api.get('/api/groups'),
  // Accepts either a name string or {name}; backend expects {name}
  createGroup: (data) => api.post('/api/groups', typeof data === 'string' ? { name: data } : data),
  // Accepts either a code string or {code}; backend expects {code}
  joinGroup: (data) => api.post('/api/groups/join', typeof data === 'string' ? { code: data } : data),
  leaveGroup: (groupId) => api.post(`/api/groups/${groupId}/leave`),
  getGroupMembers: (groupId) => api.get(`/api/groups/${groupId}/members`),
  getGroupLeaderboard: (groupId, quizIndex) => api.get(`/api/groups/${groupId}/leaderboard`, { params: { quiz_index: quizIndex } }),
  sendGroupInvite: (groupId, email, message) => api.post(`/api/groups/${groupId}/invite`, {
    group_id: groupId,
    email: email,
    message: message
  }),
  
  // Profile
  getProfile: () => api.get('/api/profile'),
  updateProfile: (data) => api.put('/api/profile', data),
  deleteAccount: () => api.delete('/api/auth/account'),

  // Badges
  getBadges: () => api.get('/api/badges'),
  getMyBadges: () => api.get('/api/badges'),
  getAllBadges: () => api.get('/api/badges/all'),
  
  // Rewards
  getRewardsInfo: () => api.get('/api/rewards/info'),
  getMyRewardsStats: (year, month) => api.get('/api/rewards/my-stats', { params: { year, month } }),
  getRewardsLeaderboard: (year, month) => api.get('/api/rewards/leaderboard', { params: { year, month } }),
  requestPayout: (bankAccount) => api.post('/api/rewards/request-payout', { bank_account: bankAccount }),
  
  // Referrals
  getReferralStats: () => api.get('/api/referrals'),
  getCurrentLeague: () => api.get('/api/leagues/current'),
  getPracticeQuestions: (params) => api.get('/api/practice/questions', { params }),
  reportQuestion: (questionId, data) => api.post(`/api/questions/${questionId}/report`, data),
  
  // Learning Analytics
  getDashboard: () => api.get('/api/learning/dashboard', { params: { lang: apiLang() } }),
  getAssessmentAnalysis: (quizIndex) => api.get(`/api/learning/assessment/${quizIndex}/analysis`, { params: { lang: apiLang() } }),
  getAiExplanation: (data) => api.post('/api/learning/ai-explain', data),
  
  // Public Ads Config
  getAdsConfig: () => api.get('/api/ads/config'),
  getManualAds: (type) => api.get('/api/ads/manual', { params: type ? { ad_type: type } : {} }),
};
